/**
 * ConversationSettingsPanel Component
 *
 * Sidebar panel for conversation settings including therapy lens selection
 * and speaker label customization. Used in the reflection detail page.
 */

import { useState } from "react";
import {
  Settings,
  Sparkles,
  Users,
  Check,
  X,
  Edit2,
  ChevronDown,
  Eye,
  AlertTriangle,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTherapyStyles } from "@/hooks/useTherapyStyles";
import type { SpeakerResponse } from "@/types/transcript";

interface ConversationSettingsPanelProps {
  /** Currently selected therapy style ID */
  therapyStyleId: string | null;
  /** Callback when therapy style changes */
  onTherapyStyleChange?: (styleId: string) => void;
  /** Speakers in the conversation */
  speakers: SpeakerResponse[];
  /** Callback when speaker name is updated */
  onSpeakerNameUpdate?: (speakerId: string, newName: string) => void;
  /** Whether settings are read-only */
  isReadOnly?: boolean;
  /** Callback when user wants to review speaker binding */
  onReviewSpeakers?: () => void;
  /** Whether speaker binding has been confirmed by user */
  speakersConfirmed?: boolean;
}

export function ConversationSettingsPanel({
  therapyStyleId,
  onTherapyStyleChange,
  speakers,
  onSpeakerNameUpdate,
  isReadOnly = false,
  onReviewSpeakers,
  speakersConfirmed = true,
}: ConversationSettingsPanelProps) {
  const { data: therapyStyles, isLoading: stylesLoading } = useTherapyStyles();
  const [editingSpeakerId, setEditingSpeakerId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [isTherapyDropdownOpen, setIsTherapyDropdownOpen] = useState(false);
  const [pendingStyleChange, setPendingStyleChange] = useState<string | null>(
    null
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const selectedStyle = therapyStyles?.find((s) => s.id === therapyStyleId);
  const pendingStyle = therapyStyles?.find((s) => s.id === pendingStyleChange);

  // Handle style selection - show confirmation if changing from existing style
  const handleStyleSelect = (styleId: string) => {
    setIsTherapyDropdownOpen(false);

    // If same style selected, do nothing
    if (styleId === therapyStyleId) return;

    // If there's an existing style, show confirmation
    if (therapyStyleId) {
      setPendingStyleChange(styleId);
      setShowConfirmModal(true);
    } else {
      // No existing style, just apply directly
      onTherapyStyleChange?.(styleId);
    }
  };

  // Confirm the style change
  const handleConfirmStyleChange = () => {
    if (pendingStyleChange) {
      onTherapyStyleChange?.(pendingStyleChange);
    }
    setShowConfirmModal(false);
    setPendingStyleChange(null);
  };

  // Cancel the style change
  const handleCancelStyleChange = () => {
    setShowConfirmModal(false);
    setPendingStyleChange(null);
  };

  const handleSpeakerEditStart = (speaker: SpeakerResponse) => {
    if (isReadOnly) return;
    setEditingSpeakerId(speaker.id);
    setEditingName(speaker.custom_name || speaker.display_label);
  };

  const handleSpeakerEditSave = () => {
    if (editingSpeakerId && editingName.trim() && onSpeakerNameUpdate) {
      onSpeakerNameUpdate(editingSpeakerId, editingName.trim());
    }
    setEditingSpeakerId(null);
    setEditingName("");
  };

  const handleSpeakerEditCancel = () => {
    setEditingSpeakerId(null);
    setEditingName("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSpeakerEditSave();
    } else if (e.key === "Escape") {
      handleSpeakerEditCancel();
    }
  };

  const getSpeakerLabel = (speaker: SpeakerResponse) => {
    return speaker.custom_name || speaker.display_label;
  };

  return (
    <Card className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2 text-foreground">
        <Settings className="h-5 w-5" />
        <h3 className="font-semibold">Conversation Settings</h3>
      </div>

      {/* Therapy Lens Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
          <Eye className="w-3 h-3" />
          Therapy Lens
        </div>

        <div className="relative">
          <button
            onClick={() => setIsTherapyDropdownOpen(!isTherapyDropdownOpen)}
            disabled={isReadOnly || stylesLoading}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-muted/50 hover:bg-muted border border-border rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Sparkles className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
              <span className="font-medium text-foreground truncate">
                {selectedStyle?.name || "Select therapy style"}
              </span>
            </div>
            <ChevronDown
              className={`w-3.5 h-3.5 text-muted-foreground transition-transform flex-shrink-0 ${isTherapyDropdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          {isTherapyDropdownOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsTherapyDropdownOpen(false)}
              />

              {/* Dropdown Menu */}
              <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-lg z-50 overflow-hidden">
                <div className="max-h-64 overflow-y-auto py-1">
                  {therapyStyles?.map((style) => {
                    const isSelected = therapyStyleId === style.id;
                    return (
                      <button
                        key={style.id}
                        onClick={() => handleStyleSelect(style.id)}
                        className={`
                          w-full text-left px-3 py-2 hover:bg-muted transition-colors flex items-start gap-2
                          ${isSelected ? "bg-primary/5" : ""}
                        `}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {isSelected ? (
                            <div className="w-3.5 h-3.5 rounded-full bg-primary flex items-center justify-center">
                              <Check className="w-2.5 h-2.5 text-primary-foreground" />
                            </div>
                          ) : (
                            <div className="w-3.5 h-3.5 rounded-full border-2 border-muted-foreground/30" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div
                            className={`text-xs font-medium ${isSelected ? "text-primary" : "text-foreground"}`}
                          >
                            {style.name}
                          </div>
                          {style.description && (
                            <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                              {style.description}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Speaker Labels Section */}
      {speakers.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            <Users className="w-3 h-3" />
            Speaker Labels
          </div>

          <div className="space-y-2">
            {speakers.map((speaker, index) => {
              const isEditing = editingSpeakerId === speaker.id;
              const isSpeakerA = index === 0;
              // Use custom_name if available, otherwise fall back to display_label
              const displayLetter = (
                speaker.custom_name || speaker.display_label
              )
                .charAt(0)
                .toUpperCase();

              return (
                <div
                  key={speaker.id}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  {/* Speaker Avatar */}
                  <div
                    className={`
                      w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold
                      ${isSpeakerA ? "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400" : "bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400"}
                    `}
                  >
                    {displayLetter}
                  </div>

                  {/* Label or Edit Input */}
                  {isEditing ? (
                    <div className="flex-1 flex items-center gap-1">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        className="flex-1 px-2 py-1 text-xs bg-background border border-primary rounded focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder={`Speaker ${displayLetter}`}
                      />
                      <button
                        onClick={handleSpeakerEditSave}
                        className="p-1 hover:bg-green-100 dark:hover:bg-green-900 rounded text-green-600 dark:text-green-400 transition-colors"
                        title="Save"
                        type="button"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={handleSpeakerEditCancel}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded text-red-600 dark:text-red-400 transition-colors"
                        title="Cancel"
                        type="button"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-between group/item">
                      <span className="text-sm font-medium text-foreground">
                        {getSpeakerLabel(speaker)}
                      </span>
                      {!isReadOnly && (
                        <button
                          onClick={() => handleSpeakerEditStart(speaker)}
                          className="p-1 opacity-0 group-hover/item:opacity-100 hover:bg-muted rounded transition-all"
                          title="Edit label"
                          type="button"
                        >
                          <Edit2 className="w-3 h-3 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Identify Speakers Button - only shown when NOT confirmed */}
          {onReviewSpeakers && !speakersConfirmed && (
            <div className="p-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-amber-700 dark:text-amber-300 text-xs mb-1">
                    <AlertTriangle className="w-3 h-3" />
                    <span className="font-medium">Not confirmed</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Confirm who's who in this conversation
                  </p>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  onClick={onReviewSpeakers}
                  className="bg-amber-600 dark:bg-amber-700 hover:bg-amber-700 dark:hover:bg-amber-800 text-white"
                >
                  Identify
                </Button>
              </div>
            </div>
          )}

          {!isReadOnly && !onReviewSpeakers && (
            <p className="text-xs text-muted-foreground">
              Click edit to customize speaker names
            </p>
          )}
        </div>
      )}

      {/* Confirmation Modal for Therapy Style Change */}
      {showConfirmModal && (
        <>
          {/* Modal Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={handleCancelStyleChange}
          />

          {/* Modal Content */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-background border border-border rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground">
                    Change Therapy Lens?
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Changing the therapy lens will regenerate all analysis and
                    feedback for this conversation. This is an expensive
                    operation and any existing insights will be replaced.
                  </p>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Current:</span>
                  <span className="font-medium text-foreground">
                    {selectedStyle?.name || "None"}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-muted-foreground">New:</span>
                  <span className="font-medium text-primary">
                    {pendingStyle?.name}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={handleCancelStyleChange}>
                  Cancel
                </Button>
                <Button onClick={handleConfirmStyleChange}>Change Lens</Button>
              </div>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
